/**
 * FabricForge — Colour-aware gallery filter (v4)
 * ------------------------------------------------------------------
 * Reads the selected colour directly from Horizon's variant picker
 * (inputs carry data-option-name="Color"/"Colour"), and re-applies the
 * filter via MutationObserver whenever Horizon morphs the gallery DOM
 * after a variant change — so the section re-render can never wipe the
 * filter (it triggers it instead).
 *
 * Gallery items are matched by image alt text; pagination dots are
 * hidden in step with their slides. Fail-safe: never blanks a gallery.
 *
 * Safety: only toggles [hidden]/display/aria-hidden on existing
 * elements. Creates nothing, positions nothing, never touches the
 * picker's DOM. (v1's mobile tap-overlay bug is structurally
 * impossible here.)
 */

(function () {
  var applying = false;
  var debounceTimer = null;

  function getColourState() {
    var inputs = Array.prototype.slice.call(
      document.querySelectorAll('variant-picker input[type="radio"][data-option-name]')
    ).filter(function (i) {
      return /colou?r/i.test(i.getAttribute('data-option-name') || '');
    });

    if (inputs.length) {
      var values = [];
      var selected = null;
      inputs.forEach(function (i) {
        var v = (i.value || '').trim().toLowerCase();
        if (v && values.indexOf(v) === -1) values.push(v);
        if (i.checked && v) selected = v;
      });
      if (selected && values.length >= 2) return { selected: selected, values: values };
    }

    // Dropdown mode fallback
    var selects = Array.prototype.slice.call(
      document.querySelectorAll('variant-picker select[name^="options["]')
    ).filter(function (s) {
      return /colou?r/i.test(s.name || '');
    });
    if (selects.length) {
      var s = selects[0];
      var vals = Array.prototype.slice.call(s.options).map(function (o) {
        return (o.value || '').trim().toLowerCase();
      }).filter(Boolean);
      var sel = (s.value || '').trim().toLowerCase();
      if (sel && vals.length >= 2) return { selected: sel, values: vals };
    }

    return null;
  }

  function apply() {
    var state = getColourState();
    if (!state) return;

    var selected = state.selected;
    var others = state.values.filter(function (v) { return v !== selected; });

    var slides = Array.prototype.slice.call(
      document.querySelectorAll('media-gallery slideshow-slide')
    );
    var gridItems = Array.prototype.slice.call(
      document.querySelectorAll('media-gallery .media-gallery__grid > li')
    );
    if (!slides.length && !gridItems.length) return;

    function decide(el) {
      var img = el.querySelector('img');
      var alt = ((img && img.alt) || '').trim().toLowerCase();
      if (!alt) return false;
      if (alt.indexOf(selected) !== -1) return false;
      for (var i = 0; i < others.length; i++) {
        if (alt.indexOf(others[i]) !== -1) return true;
      }
      return false;
    }

    var slideHide = slides.map(decide);
    var gridHide = gridItems.map(decide);

    if (slides.length && slideHide.every(Boolean)) slideHide = slideHide.map(function () { return false; });
    if (gridItems.length && gridHide.every(Boolean)) gridHide = gridHide.map(function () { return false; });

    applying = true;
    slides.forEach(function (el, i) { setHidden(el, slideHide[i]); });
    gridItems.forEach(function (el, i) { setHidden(el, gridHide[i]); });

    var dotContainers = document.querySelectorAll(
      'media-gallery slideshow-controls .slideshow-controls__dots'
    );
    dotContainers.forEach(function (dc) {
      var dots = Array.prototype.slice.call(dc.children);
      if (dots.length === slides.length) {
        dots.forEach(function (d, i) { setHidden(d, slideHide[i]); });
      }
    });
    applying = false;

    window.dispatchEvent(new Event('resize'));
  }

  function setHidden(el, hide) {
    el.toggleAttribute('hidden', hide);
    el.style.display = hide ? 'none' : '';
    el.setAttribute('aria-hidden', hide ? 'true' : 'false');
  }

  function queueApply() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(apply, 100);
  }

  function init() {
    if (!document.querySelector('variant-picker')) return;

    // Re-apply whenever the gallery/section DOM is morphed by the theme.
    // We only watch childList changes, and our own updates only touch
    // attributes, so this cannot loop.
    var observer = new MutationObserver(function (mutations) {
      if (applying) return;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var target = m.target;
        if (target && target.nodeType === 1) {
          if (
            target.closest &&
            (target.closest('media-gallery') || target.closest('variant-picker') ||
             (target.querySelector && target.querySelector('media-gallery')))
          ) {
            queueApply();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && (t.matches('input[type="radio"]') || t.matches('select'))) queueApply();
    });

    apply();
    setTimeout(apply, 400);
    setTimeout(apply, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

function altMatchesColour(alt, colour) {
  if (!alt || !colour) return false;
  return String(alt).toLowerCase().indexOf(String(colour).trim().toLowerCase()) !== -1;
}

class FFVariantGallery {}

export { FFVariantGallery, altMatchesColour };
